import { Injectable } from '@angular/core';
import { Subject, BehaviorSubject, Observable } from 'rxjs';
import type { Map as MapLibreMap, LayerSpecification } from 'maplibre-gl';
import {
  KMLConvertedData,
  KMLGroundOverlay,
  KMLLayerStyleConfig,
  GeoJSONFeatureCollection,
  MapLibreImageSource,
  MapLibreImageLayer,
} from '../models/kml-geojson.model';

/**
 * MapLibre GL JS layer management service for KML visualization
 * Handles rendering of both vector (GeoJSON) and raster (GroundOverlay) data
 *
 * REQUIRES: maplibre-gl package to be installed
 * Install: npm install maplibre-gl
 *
 * Usage:
 * 1. Initialize map instance via Angular component
 * 2. Call methods to add layers, sources, and overlays
 * 3. Service manages lifecycle and updates
 */
@Injectable({
  providedIn: 'root',
})
export class MapLibreKMLService {
  private readonly layerUpdateSubject = new Subject<void>();
  private readonly overlayUpdateSubject = new BehaviorSubject<KMLGroundOverlay[]>([]);
  private readonly overlayImageBlobUrls = new Map<string, string>();

  get layerUpdated$(): Observable<void> {
    return this.layerUpdateSubject.asObservable();
  }

  get overlayUpdated$(): Observable<KMLGroundOverlay[]> {
    return this.overlayUpdateSubject.asObservable();
  }

  /**
   * Add KML-converted data (GeoJSON + GroundOverlays) to MapLibre map
   *
   * @param map MapLibre GL JS map instance
   * @param kmlData Converted KML data with both vector and raster content
   * @param sourceId Unique identifier for the source
   * @param layerConfig Optional styling and behavior configuration
   */
  async addKMLDataToMap(
    map: MapLibreMap,
    kmlData: KMLConvertedData,
    sourceId: string,
    layerConfig?: KMLLayerStyleConfig,
  ): Promise<void> {
    if (!map) {
      throw new Error('MapLibre map instance required');
    }

    // Add vector data (GeoJSON)
    if (kmlData.vectorData && kmlData.vectorData.features.length > 0) {
      this.addGeoJSONLayer(map, kmlData.vectorData, sourceId, layerConfig);
    }

    // Add raster data (GroundOverlays)
    if (kmlData.overlays && kmlData.overlays.length > 0) {
      await Promise.all(
        kmlData.overlays.map((overlay, index) =>
          this.addGroundOverlay(map, overlay, `${sourceId}-overlay-${index}`),
        ),
      );
      this.overlayUpdateSubject.next(kmlData.overlays);
    }

    this.layerUpdateSubject.next();
  }

  /**
   * Add GeoJSON vector layer to map
   * Handles Points, LineStrings, and Polygons
   */
  private addGeoJSONLayer(
    map: MapLibreMap,
    geojson: GeoJSONFeatureCollection,
    sourceId: string,
    config?: KMLLayerStyleConfig,
  ): void {
    // Add source
    if (!map.getSource(sourceId)) {
      map.addSource(sourceId, {
        type: 'geojson',
        data: geojson,
      });
    }

    // Add layers for different geometry types
    this.addPointLayer(map, sourceId, config);
    this.addLineLayer(map, sourceId, config);
    this.addPolygonLayer(map, sourceId, config);
  }

  /**
   * Add Point features layer
   */
  private addPointLayer(map: MapLibreMap, sourceId: string, config?: KMLLayerStyleConfig): void {
    const layerId = `${sourceId}-points`;
    if (!map.getLayer(layerId)) {
      map.addLayer({
        id: layerId,
        type: 'circle',
        source: sourceId,
        filter: ['==', ['geometry-type'], 'Point'],
        paint: {
          'circle-radius': config?.pointSize || 6,
          'circle-color': config?.pointColor || '#3b82f6',
          'circle-opacity': config?.fillOpacity ?? 0.8,
          'circle-stroke-width': 1,
          'circle-stroke-color': '#ffffff',
        },
      });
    }
  }

  /**
   * Add LineString features layer
   */
  private addLineLayer(map: MapLibreMap, sourceId: string, config?: KMLLayerStyleConfig): void {
    const layerId = `${sourceId}-lines`;
    if (!map.getLayer(layerId)) {
      map.addLayer({
        id: layerId,
        type: 'line',
        source: sourceId,
        filter: ['==', ['geometry-type'], 'LineString'],
        paint: {
          'line-color': config?.strokeColor || '#ef4444',
          'line-width': config?.strokeWidth || 2,
          'line-opacity': config?.fillOpacity ?? 0.8,
        },
      });
    }
  }

  /**
   * Add Polygon features layer
   */
  private addPolygonLayer(map: MapLibreMap, sourceId: string, config?: KMLLayerStyleConfig): void {
    const layerId = `${sourceId}-polygons`;
    if (!map.getLayer(layerId)) {
      map.addLayer({
        id: layerId,
        type: 'fill',
        source: sourceId,
        filter: ['==', ['geometry-type'], 'Polygon'],
        paint: {
          'fill-color': config?.fillColor || '#10b981',
          'fill-opacity': config?.fillOpacity ?? 0.5,
        },
      });

      // Add stroke layer for polygons
      const strokeLayerId = `${sourceId}-polygons-stroke`;
      map.addLayer({
        id: strokeLayerId,
        type: 'line',
        source: sourceId,
        filter: ['==', ['geometry-type'], 'Polygon'],
        paint: {
          'line-color': config?.strokeColor || '#059669',
          'line-width': config?.strokeWidth || 1,
          'line-opacity': 0.8,
        },
      });
    }
  }

  /**
   * Add GroundOverlay (raster) to map
   * Uses MapLibre image source with corner coordinates
   *
   * @param map MapLibre GL JS map instance
   * @param overlay GroundOverlay metadata
   * @param sourceId Unique identifier for this overlay source
   */
  private async addGroundOverlay(
    map: MapLibreMap,
    overlay: KMLGroundOverlay,
    sourceId: string,
  ): Promise<void> {
    try {
      const imageUrl = await this.resolveOverlayImageUrl(overlay.imageUrl, sourceId);

      // Map KML coordinates to image source corners [NW, NE, SE, SW]
      const imageSource: MapLibreImageSource = {
        type: 'image',
        url: imageUrl,
        coordinates: [
          [overlay.west, overlay.north], // NW
          [overlay.east, overlay.north], // NE
          [overlay.east, overlay.south], // SE
          [overlay.west, overlay.south], // SW
        ],
      };

      // Add image source
      if (!map.getSource(sourceId)) {
        map.addSource(sourceId, imageSource);
      }

      // Add raster layer
      const layerId = `${sourceId}-layer`;
      if (!map.getLayer(layerId)) {
        const layer: MapLibreImageLayer = {
          id: layerId,
          type: 'raster',
          source: sourceId,
          paint: {
            'raster-opacity': overlay.opacity ?? 1.0,
          },
        };
        map.addLayer(layer);
      }
    } catch (error) {
      throw new Error(`Failed to add overlay ${overlay.name}: ${(error as Error).message}`);
    }
  }

  /**
   * A GroundOverlay's uploaded image sometimes has malformed chunk framing (e.g. an `IDAT`
   * length longer than the file itself) or a generic `Content-Type` (`binary/octet-stream`
   * instead of `image/*`). MapLibre decodes image sources via `createImageBitmap()`, which is
   * strict about both — it throws `InvalidStateError: The source image could not be decoded`
   * on bytes an `<img>` element (and its lenient, sniffing decoder) renders without complaint.
   * Decoding through an `<img>` and re-encoding via canvas produces a clean, correctly-typed
   * PNG that `createImageBitmap` can actually decode.
   */
  private async resolveOverlayImageUrl(url: string, sourceId: string): Promise<string> {
    let rawBlobUrl: string | undefined;
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      rawBlobUrl = URL.createObjectURL(blob);

      const healedUrl = await this.reencodeImage(rawBlobUrl);
      this.revokeTrackedBlobUrl(sourceId);
      this.overlayImageBlobUrls.set(sourceId, healedUrl);
      return healedUrl;
    } catch {
      return url;
    } finally {
      if (rawBlobUrl) URL.revokeObjectURL(rawBlobUrl);
    }
  }

  /** Decodes `blobUrl` through an `<img>` element and re-exports it as a fresh PNG blob URL. */
  private reencodeImage(blobUrl: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = image.naturalWidth;
        canvas.height = image.naturalHeight;
        const context = canvas.getContext('2d');
        if (!context) {
          reject(new Error('Canvas 2D context unavailable'));
          return;
        }
        context.drawImage(image, 0, 0);
        canvas.toBlob((healedBlob) => {
          if (!healedBlob) {
            reject(new Error('Canvas could not re-encode the image'));
            return;
          }
          resolve(URL.createObjectURL(healedBlob));
        }, 'image/png');
      };
      image.onerror = () => reject(new Error('Image element could not decode the source'));
      image.src = blobUrl;
    });
  }

  private revokeTrackedBlobUrl(sourceId: string): void {
    const existing = this.overlayImageBlobUrls.get(sourceId);
    if (existing) {
      URL.revokeObjectURL(existing);
      this.overlayImageBlobUrls.delete(sourceId);
    }
  }

  /**
   * Revokes every overlay blob URL this root singleton is still tracking. A component
   * navigating away destroys its map before calling removeKMLLayer() on each of its own
   * sources, so this is the only point that reliably releases blob URLs left over from
   * that session rather than accumulating them across every navigate-away/navigate-back cycle.
   */
  clearAllOverlayBlobUrls(): void {
    this.overlayImageBlobUrls.forEach((blobUrl) => URL.revokeObjectURL(blobUrl));
    this.overlayImageBlobUrls.clear();
  }

  /**
   * The layer id suffixes this service derives from a source id. A GroundOverlay gets
   * its own source (`{sourceId}-overlay-{n}`) and therefore its own layer, so matching
   * on the `{sourceId}` prefix alone would tear an overlay's layer off while removing
   * the parent source and leave that source behind without one.
   */
  private static readonly SOURCE_LAYER_SUFFIXES = [
    'points',
    'lines',
    'polygons',
    'polygons-stroke',
    'layer',
  ];

  private belongsToSource(layer: LayerSpecification, sourceId: string): boolean {
    if ('source' in layer && layer.source === sourceId) {
      return true;
    }
    return MapLibreKMLService.SOURCE_LAYER_SUFFIXES.some(
      (suffix) => layer.id === `${sourceId}-${suffix}`,
    );
  }

  /**
   * Remove KML layer and all associated sources
   */
  removeKMLLayer(map: MapLibreMap, sourceId: string): void {
    try {
      // Remove all layers associated with this source
      const layers = map.getStyle().layers;
      if (layers) {
        layers
          .filter((layer: LayerSpecification) => this.belongsToSource(layer, sourceId))
          .forEach((layer: LayerSpecification) => {
            map.removeLayer(layer.id);
          });
      }

      // Remove source
      if (map.getSource(sourceId)) {
        map.removeSource(sourceId);
      }
      this.revokeTrackedBlobUrl(sourceId);

      this.layerUpdateSubject.next();
    } catch (error) {
      throw new Error(`Failed to remove KML layer ${sourceId}: ${(error as Error).message}`);
    }
  }

  /**
   * Update GroundOverlay opacity
   */
  updateOverlayOpacity(
    map: MapLibreMap,
    sourceId: string,
    opacity: number,
  ): void {
    const layerId = `${sourceId}-layer`;
    if (map.getLayer(layerId)) {
      map.setPaintProperty(layerId, 'raster-opacity', Math.max(0, Math.min(1, opacity)));
    }
  }

  /**
   * Get current overlays
   */
  getCurrentOverlays(): Observable<KMLGroundOverlay[]> {
    return this.overlayUpdated$;
  }

  /**
   * Clear all overlays
   */
  clearOverlays(): void {
    this.overlayUpdateSubject.next([]);
  }
}
